import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon, StarIcon, ClockIcon, ExclamationTriangleIcon,
  ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Avatar from '../components/Avatar';
import { adminAPI } from '../services/api';
import styles from './CRMPage.module.css';

// Two families of segment, and the difference matters.
//
// The app segments count people who installed the app -- 183 of them. The
// shop has 24,915 customers in Business Central, and the groups worth
// campaigning to live almost entirely outside the app: 2,349 paying VIPs
// have never installed it. "Expiring in 30 days" read 3 when 113 memberships
// were about to lapse, which is a renewal campaign nobody was sending.
//
// Both are kept and labelled, because they answer different questions: the
// app ones are about engagement with the app, the shop ones about the
// membership business.
const SEGMENTS = [
  {
    key: 'registered_no_vip',
    label: 'Registered — No VIP',
    description: 'Users who downloaded & registered but never purchased a VIP membership.',
    icon: UserGroupIcon,
    color: '#6366F1',
    bg: 'rgba(99,102,241,0.1)',
    campaign: 'Convert to VIP',
  },
  {
    key: 'vip_no_sales',
    label: 'VIP — No Purchases Yet',
    description: 'Active VIP members with no purchase in store since joining.',
    icon: StarIcon,
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.1)',
    campaign: 'Drive First Purchase',
  },
  {
    key: 'expiring_30',
    label: 'Expiring in 30 Days',
    description: 'VIP memberships expiring within the next 30 days — urgent renewal window.',
    icon: ExclamationTriangleIcon,
    color: '#EF4444',
    bg: 'rgba(239,68,68,0.1)',
    campaign: 'Urgent Renewal',
  },
  {
    key: 'expiring_60',
    label: 'Expiring in 60 Days',
    description: 'VIP memberships expiring within 31–60 days — proactive renewal campaign.',
    icon: ClockIcon,
    color: '#F97316',
    bg: 'rgba(249,115,22,0.1)',
    campaign: 'Renewal Reminder',
  },
  {
    key: 'churned',
    label: 'Churned — Expired VIP',
    description: 'Members whose VIP expired and who have not taken a new one — win-back opportunity.',
    icon: ArrowPathIcon,
    color: '#64748B',
    bg: 'rgba(100,116,139,0.1)',
    campaign: 'Win-Back Campaign',
  },
];

// Everybody the shop knows about, whether or not they ever installed the app.
const BC_SEGMENTS = [
  {
    key: 'bc_expiring_30',
    label: 'Expiring in 30 Days',
    description: 'Every VIP membership in Business Central lapsing within 30 days, app or not.',
    icon: ExclamationTriangleIcon,
    color: '#DC2626',
    bg: 'rgba(220,38,38,0.1)',
    campaign: 'Urgent Renewal',
    countKey: 'bcExpiring30',
  },
  {
    key: 'bc_expiring_60',
    label: 'Expiring in 31–60 Days',
    description: 'The month after next — time to remind them before it is urgent.',
    icon: ClockIcon,
    color: '#EA580C',
    bg: 'rgba(234,88,12,0.1)',
    campaign: 'Renewal Reminder',
    countKey: 'bcExpiring60',
  },
  {
    key: 'bc_lapsed',
    label: 'Lapsed VIP',
    description: 'Their membership has already run out and they have not renewed.',
    icon: ArrowPathIcon,
    color: '#64748B',
    bg: 'rgba(100,116,139,0.1)',
    campaign: 'Win-Back Campaign',
    countKey: 'bcLapsed',
  },
  {
    key: 'bc_vip_no_app',
    label: 'VIP without the App',
    description: 'Paying members who have never installed it. The largest single group of people already giving you money.',
    icon: StarIcon,
    color: '#D97706',
    bg: 'rgba(217,119,6,0.1)',
    campaign: 'Get Them On The App',
    countKey: 'bcVipNoApp',
  },
  {
    key: 'bc_silver_with_app',
    label: 'Has the App, Not VIP',
    description: 'They installed it and never bought a membership. Already interested.',
    icon: UserGroupIcon,
    color: '#4F46E5',
    bg: 'rgba(79,70,229,0.1)',
    campaign: 'Convert to VIP',
    countKey: 'bcSilverWithApp',
  },
  {
    key: 'bc_silver_no_app',
    label: 'Customers, Not Members',
    description: 'Everybody else the shop has a number for. The pool every other segment is grown from.',
    icon: UserGroupIcon,
    color: '#0891B2',
    bg: 'rgba(8,145,178,0.1)',
    campaign: 'Introduce VIP',
    countKey: 'bcSilverNoApp',
  },
];

function DaysLeftBadge({ days }) {
  if (days === null || days === undefined || days === 0) return null;
  const color = days <= 7 ? '#EF4444' : days <= 30 ? '#F59E0B' : '#22C55E';
  return <span style={{ color, fontWeight: 600, fontSize: 13 }}>{days}d</span>;
}

export default function CRMPage() {
  const navigate = useNavigate();
  const [counts, setCounts]         = useState(null);
  const [active, setActive]         = useState(null);
  const [members, setMembers]       = useState([]);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [countLoading, setCountLoading] = useState(true);
  const [exporting, setExporting]   = useState(false);

  useEffect(() => {
    adminAPI.getCRMSegments().then(r => { setCounts(r.segments); setCountLoading(false); }).catch(() => setCountLoading(false));
  }, []);

  const loadSegment = useCallback(async (key, p = 1) => {
    setLoading(true);
    try {
      const r = await adminAPI.getCRMSegment(key, p);
      setMembers(r.members);
      setTotal(r.total);
      setTotalPages(r.totalPages);
      setPage(p);
    } finally { setLoading(false); }
  }, []);

  const selectSegment = (seg) => {
    setActive(seg);
    setPage(1);
    loadSegment(seg.key, 1);
  };

  const exportCSV = async () => {
    if (!active) return;
    setExporting(true);
    try {
      const r = await adminAPI.exportCRMSegment(active.key);
      const url = URL.createObjectURL(new Blob([r], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `crm-${active.key}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const activeMeta = [...SEGMENTS, ...BC_SEGMENTS].find(s => s.key === active?.key);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>CRM Segments</h1>
          <p className={styles.pageSub}>Target members by lifecycle stage to run focused campaigns.</p>
        </div>
      </div>

      {/* The shop first: these are the numbers the business runs on, and the
          app segments underneath them answer a narrower question. Putting
          the 183 app users above the 24,915 customers was what made
          "expiring in 30 days" look like 3. */}
      <h2 style={{ fontSize:15, margin:'4px 0 2px', color:'var(--text)' }}>
        Everyone in Business Central
      </h2>
      <p style={{ fontSize:13, color:'var(--text-muted)', margin:'0 0 12px' }}>
        The whole customer base, whether or not they have the app.
        {counts && <> {Number(counts.bcVip || 0).toLocaleString()} VIP members in all.</>}
      </p>
      <div className={styles.segmentGrid}>
        {BC_SEGMENTS.map(seg => {
          const Icon = seg.icon;
          const count = counts ? counts[seg.countKey] : null;
          const isActive = active?.key === seg.key;
          return (
            <button
              key={seg.key}
              className={`${styles.segCard} ${isActive ? styles.segCardActive : ''}`}
              style={isActive ? { borderColor: seg.color, boxShadow: `0 0 0 3px ${seg.color}22` } : {}}
              onClick={() => selectSegment(seg)}
            >
              <div className={styles.segCardTop}>
                <div className={styles.segIcon} style={{ background: seg.bg, color: seg.color }}>
                  <Icon width={20} height={20} />
                </div>
                {isActive && <CheckCircleIcon width={18} height={18} style={{ color: seg.color }} />}
              </div>
              <div className={styles.segCount} style={{ color: seg.color }}>
                {count === null ? '—' : count.toLocaleString()}
              </div>
              <div className={styles.segLabel}>{seg.label}</div>
              <div className={styles.segDesc}>{seg.description}</div>
              <div className={styles.segCampaign} style={{ background: seg.bg, color: seg.color }}>
                {seg.campaign}
              </div>
            </button>
          );
        })}
      </div>

      <h2 style={{ fontSize:15, margin:'26px 0 2px', color:'var(--text)' }}>
        App members only
      </h2>
      <p style={{ fontSize:13, color:'var(--text-muted)', margin:'0 0 12px' }}>
        People who installed the app. Useful for app engagement, far smaller than the shop.
      </p>
      <div className={styles.segmentGrid}>
        {SEGMENTS.map(seg => {
          const Icon = seg.icon;
          const count = counts ? counts[seg.key === 'registered_no_vip' ? 'registeredNoVip' : seg.key === 'vip_no_sales' ? 'vipNoSales' : seg.key === 'expiring_30' ? 'expiring30' : seg.key === 'expiring_60' ? 'expiring60' : 'churned'] : null;
          const isActive = active?.key === seg.key;
          return (
            <button
              key={seg.key}
              className={`${styles.segCard} ${isActive ? styles.segCardActive : ''}`}
              style={isActive ? { borderColor: seg.color, boxShadow: `0 0 0 3px ${seg.color}22` } : {}}
              onClick={() => selectSegment(seg)}
            >
              <div className={styles.segCardTop}>
                <div className={styles.segIcon} style={{ background: seg.bg, color: seg.color }}>
                  <Icon width={20} height={20} />
                </div>
                {isActive && <CheckCircleIcon width={18} height={18} style={{ color: seg.color }} />}
              </div>
              <div className={styles.segCount} style={{ color: seg.color }}>
                {countLoading ? '—' : count ?? '—'}
              </div>
              <div className={styles.segLabel}>{seg.label}</div>
              <div className={styles.segDesc}>{seg.description}</div>
              <div className={styles.segCampaignTag} style={{ background: seg.bg, color: seg.color }}>
                {seg.campaign}
              </div>
            </button>
          );
        })}
      </div>

      {/* Member list */}
      {active && (
        <div className={styles.listCard}>
          <div className={styles.listHeader}>
            <div>
              <h2 className={styles.listTitle}>{activeMeta?.label}</h2>
              <p className={styles.listSub}>{total} member{total !== 1 ? 's' : ''} in this segment</p>
            </div>
            <button className="btn-primary" onClick={exportCSV} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowDownTrayIcon width={15} height={15} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>

          <div className={styles.tableWrap}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Mobile</th>
                  <th>Member Code</th>
                  <th>Registered</th>
                  <th>Expiry</th>
                  <th>Days Left</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j}><div className="skeleton" style={{ height: 16, width: j === 0 ? 140 : 80 }} /></td>
                      ))}
                    </tr>
                  ))
                ) : members.length === 0 ? (
                  <tr><td colSpan={7}>
                    <div className="empty-state">
                      <p>No members in this segment.</p>
                    </div>
                  </td></tr>
                ) : members.map(m => (
                  <tr key={m.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={`${m.first_name} ${m.last_name}`} size={32} />
                        <span style={{ fontWeight: 600 }}>{m.first_name} {m.last_name}</span>
                      </div>
                    </td>
                    <td>{m.mobile}</td>
                    <td>{m.member_code ? <code style={{ fontSize: 12, background: 'var(--bg-subtle)', padding: '2px 6px', borderRadius: 4 }}>{m.member_code}</code> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</td>
                    <td>{m.expiry_date ? new Date(m.expiry_date).toLocaleDateString() : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td><DaysLeftBadge days={m.days_remaining} /></td>
                    <td>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => navigate(`/members/${m.id}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button className="btn-ghost" onClick={() => loadSegment(active.key, page - 1)} disabled={page === 1}>
                <ChevronLeftIcon width={16} height={16} />
              </button>
              <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
              <button className="btn-ghost" onClick={() => loadSegment(active.key, page + 1)} disabled={page === totalPages}>
                <ChevronRightIcon width={16} height={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {!active && (
        <div className="empty-state" style={{ marginTop: 32 }}>
          <UserGroupIcon width={40} height={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p>Select a segment above to view members and export for campaigns.</p>
        </div>
      )}
    </div>
  );
}
